import { Baseline } from '@shared/models/project.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { projectGuards } from '../guards/project-guards.helpers';
import { getObjectHash } from '../object.helpers';

/**
 * Recursively extract hashable element data (ids, kind, property expressions, children).
 *
 * @param elements - Version elements to hash
 * @returns Serializable structure for hashing
 */
export const hashElements = (elements: Baseline['elements']): unknown[] => {
  if (!elements) return [];
  return elements.map((el) => ({
    id: el.id,
    kind: el.kind,
    properties: projectGuards.is.element(el)
      ? el.properties?.map((p: IElementProperty) => ({
          id: p.id,
          name: p.name,
          expression:
            'count' in p
              ? (p.count as { expression?: string })?.expression
              : undefined,
        }))
      : undefined,
    elements:
      projectGuards.is.element(el) && el.elements
        ? hashElements(el.elements)
        : undefined,
  }));
};

/**
 * Generate a hash for cache invalidation.
 * Includes element structure, property expressions, proposals, actions, and products
 * so any change that affects computation produces a different hash.
 *
 * @param version - The building version to hash
 * @returns Hash string for cache comparison
 */
export const generateVersionHash = (version: Baseline): string => {
  const hashData = {
    id: version.id,
    proposals: version.proposals?.map((p) => ({
      id: p.id,
      actionIds: p.actionIds,
    })),
    // Full change payloads so hash changes when any field changes (not only change count)
    actions: version.actions?.map((a) => ({
      id: a.id,
      name: a.name,
      changes: a.changes,
    })),
    elements: hashElements(version.elements),
    products: version.products ? Object.keys(version.products).sort() : [],
  };
  return getObjectHash(hashData);
};
